import * as React from "react"
import { AuthProvider, useAuth } from "@/contexts/AuthContext"
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom"
import { Onboarding } from "@/pages/Onboarding"
import { AuthPage } from "@/pages/AuthPage"
import { Dashboard } from "@/pages/Dashboard"
import { AdminPage } from "@/pages/AdminPage"
import { WelcomeSpinPage } from "@/pages/WelcomeSpinPage"
import { RetroBackgroundSimple } from "@/components/RetroBackground"
import { motion } from "motion/react"
import ovalMoneyClubLogo from "@/public/oval-money-club-logo.png"
import coinImg from "@/public/coin.png"
import "./index.css"

const hasAdminPortalAccess = (role?: string | null) => role === "super_admin" || role === "support"
const isSupportRole = (role?: string | null) => role === "support"

function InvestigateModeBanner() {
  const { investigation, user, endInvestigateMode } = useAuth()
  const [isEnding, setIsEnding] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  if (!investigation?.isActive) {
    return null
  }

  const handleEndNow = async () => {
    setIsEnding(true)
    setError(null)

    const result = await endInvestigateMode()
    if (!result.success) {
      setError(result.message || "Failed to end investigate mode")
      setIsEnding(false)
      return
    }

    setIsEnding(false)
    if (!location.pathname.startsWith("/admin")) {
      navigate("/admin/settings", { replace: true })
    }
  }

  return (
    <div className="fixed top-3 left-1/2 -translate-x-1/2 z-[80] w-[min(960px,calc(100vw-1rem))] px-2">
      <div className="rounded-2xl border border-[#F37020]/40 bg-[#120D09]/95 shadow-[0_12px_45px_rgba(0,0,0,0.45)] backdrop-blur-md">
        <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#F6A56E] font-semibold">
              <span className="inline-block h-2 w-2 rounded-full bg-[#F37020] animate-pulse" />
              Investigate Mode
            </div>
            <p className="text-sm text-white/85 mt-1 truncate">
              Viewing as <span className="font-semibold text-white">{user?.email || "customer"}</span>
              <span className="text-white/35"> via {investigation.actorRole === "super_admin" ? "super admin" : "support"} </span>
              <span className="text-white/65">{investigation.actorEmail}</span>
            </p>
            {error && <p className="text-xs text-red-300/90 mt-1">{error}</p>}
          </div>

          <button
            type="button"
            onClick={handleEndNow}
            disabled={isEnding}
            className="shrink-0 inline-flex items-center justify-center rounded-xl border border-[#F37020]/35 bg-[#F37020]/15 px-3 py-2 text-sm font-semibold text-[#FDB37E] hover:bg-[#F37020]/25 transition-colors disabled:opacity-60"
          >
            {isEnding ? "Ending..." : "End now"}
          </button>
        </div>
      </div>
    </div>
  )
}

function LoadingScreen() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <RetroBackgroundSimple />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", bounce: 0.4 }}
        className="text-center relative z-10"
      >
        {/* Floating coin */}
        <motion.div
          className="mx-auto mb-6"
          animate={{
            y: [0, -15, 0],
            rotate: [0, 360],
          }}
          transition={{
            y: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
            rotate: { duration: 3, repeat: Infinity, ease: "linear" },
          }}
        >
          <img src={coinImg} alt="" className="w-20 h-20 drop-shadow-lg" />
        </motion.div>

        {/* Logo */}
        <motion.button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mx-auto mb-4 block rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F37020] focus-visible:ring-offset-2 focus-visible:ring-offset-[#121220]"
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          aria-label="Go to dashboard"
        >
          <img
            src={ovalMoneyClubLogo}
            alt="Oval Money Club"
            className="h-10"
          />
        </motion.button>

        {/* Loading text */}
        <motion.div
          className="inline-flex items-center gap-2 px-6 py-3 bg-white border-4 border-black rounded-2xl shadow-[6px_6px_0_0_#1a1a1a]"
          animate={{ rotate: [-1, 1, -1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-5 h-5 border-4 border-[#F37020]/30 border-t-[#F37020] rounded-full"
          />
          <span className="font-bold text-lg">Loading...</span>
        </motion.div>
      </motion.div>
    </div>
  )
}

// Protected route wrapper - redirects to login if not authenticated
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}

// Auth route wrapper - redirects to dashboard if already authenticated
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (isAuthenticated) {
    if (isSupportRole(user?.role)) {
      return <Navigate to="/admin" replace />
    }

    // Determine where to redirect based on user state
    if (!user?.hasCompletedOnboarding) {
      return <Navigate to="/onboarding" replace />
    }

    const userType = user?.onboardingData?.userType
    const isAgentOrSoloAgent = userType === "agent" || userType === "solo_agent"
    const hasClaimedWelcomeReward = user?.welcomeReward?.wonAt != null

    if (isAgentOrSoloAgent && !hasClaimedWelcomeReward) {
      return <Navigate to="/welcome-spin" replace />
    }

    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Onboarding route - requires auth but not completed onboarding
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isSupportRole(user?.role)) {
    return <Navigate to="/admin" replace />
  }

  // If already completed onboarding, redirect appropriately
  if (user?.hasCompletedOnboarding) {
    const userType = user?.onboardingData?.userType
    const isAgentOrSoloAgent = userType === "agent" || userType === "solo_agent"
    const hasClaimedWelcomeReward = user?.welcomeReward?.wonAt != null

    if (isAgentOrSoloAgent && !hasClaimedWelcomeReward) {
      return <Navigate to="/welcome-spin" replace />
    }

    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Welcome spin route - requires auth, completed onboarding, but not claimed reward
function WelcomeSpinRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isSupportRole(user?.role)) {
    return <Navigate to="/admin" replace />
  }

  if (!user?.hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  const userType = user?.onboardingData?.userType
  const isAgentOrSoloAgent = userType === "agent" || userType === "solo_agent"
  const hasClaimedWelcomeReward = user?.welcomeReward?.wonAt != null

  // If not agent/solo_agent or already claimed, go to dashboard
  if (!isAgentOrSoloAgent || hasClaimedWelcomeReward) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Dashboard route - requires full completion
function DashboardRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isSupportRole(user?.role)) {
    return <Navigate to="/admin" replace />
  }

  if (!user?.hasCompletedOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  const userType = user?.onboardingData?.userType
  const isAgentOrSoloAgent = userType === "agent" || userType === "solo_agent"
  const hasClaimedWelcomeReward = user?.welcomeReward?.wonAt != null

  // If agent/solo_agent and hasn't claimed reward, go to welcome spin
  if (isAgentOrSoloAgent && !hasClaimedWelcomeReward) {
    return <Navigate to="/welcome-spin" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!hasAdminPortalAccess(user?.role)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  return (
    <>
      <InvestigateModeBanner />
      <Routes>
        {/* Auth routes */}
        <Route
          path="/login"
          element={
            <AuthRoute>
              <AuthPage />
            </AuthRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRoute>
              <AuthPage />
            </AuthRoute>
          }
        />

        {/* Onboarding */}
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <Onboarding />
            </OnboardingRoute>
          }
        />

        {/* Welcome Spin */}
        <Route
          path="/welcome-spin"
          element={
            <WelcomeSpinRoute>
              <WelcomeSpinPage />
            </WelcomeSpinRoute>
          }
        />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <DashboardRoute>
              <Dashboard />
            </DashboardRoute>
          }
        />
        <Route
          path="/dashboard/:tab"
          element={
            <DashboardRoute>
              <Dashboard />
            </DashboardRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/:tab"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
